import { Flex } from '@chakra-ui/react'
import ApexCharts from 'apexcharts'
import { useEffect, useMemo, useRef } from 'react'
import { iIndicador, iMesPeriodo } from '../..'
import { FormatarNumero } from '../../../../../Utils/Helper'

interface BarChartProps {
  id: string
  data: iMesPeriodo[]
  indicador: iIndicador
}

export const IndicadorChart: React.FC<BarChartProps> = ({ id, data, indicador }) => {
  const chartRef = useRef<ApexCharts | null>(null)

  const mesesAbreviados = [
    'Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun',
    'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'
  ]

  const dadosOrdenados = useMemo(() => {
    return [...data].sort((a, b) => a.numeroMes - b.numeroMes)
  }, [data])

  function GetAcumulado(index: number): number {
    let response = 0

    if (indicador.tipoMeta === 5) {
      return dadosOrdenados[index]?.real ?? 0
    }

    for (let i = 0; i <= index; i++) {
      response += dadosOrdenados[i]?.real ?? 0
    }

    return response
  }

  useEffect(() => {
    const element = document.querySelector(`#chart-${id}`)
    if (!element) return

    if (chartRef.current) {
      chartRef.current.destroy()
      chartRef.current = null
    }

    const options = {
      chart: {
        id: indicador.nome.replaceAll(' ', '_'),
        height: 350,
        type: 'line',
        stacked: false,
        zoom: {
          enabled: false
        }
      },
      markers: {
        size: 6
      },
      dataLabels: {
        enabled: false
      },
      colors: ['#27AE60', '#F2994A'],
      series: [
        {
          name: 'Valor Acumulado',
          data: dadosOrdenados.map((_, i) => GetAcumulado(i))
        },
        {
          name: 'Valor Previsto',
          data: dadosOrdenados.map((e) => e.previsto)
        }
      ],
      stroke: {
        width: [4, 4]
      },
      plotOptions: {
        bar: {
          columnWidth: '20%'
        }
      },
      xaxis: {
        categories: dadosOrdenados.map(e => mesesAbreviados[e.numeroMes - 1])
      },
      yaxis: {
        labels: {
          formatter: function (value: number) {
            return indicador.tipoMeta === 0
              ? `${indicador.medidaMeta ?? ''}${FormatarNumero(value, indicador)}`
              : `${FormatarNumero(value, indicador)}${indicador.medidaMeta ?? ''}`
          }
        },
        min: dadosOrdenados.some(e =>
          (e.previsto && e.previsto < 0) ?? (e.real && e.real < 0)
        )
          ? undefined
          : 0
      },
      tooltip: {
        shared: false,
        intersect: true,
        x: {
          show: false
        },
        y: {
          show: true,
          formatter: function (value: number) {
            return indicador.tipoMeta === 0
              ? `${indicador.medidaMeta ?? ''}${FormatarNumero(value, indicador)}`
              : `${FormatarNumero(value, indicador)}${indicador.medidaMeta ?? ''}`
          }
        }
      },
      legend: {
        horizontalAlign: 'left',
        offsetX: 40,
        itemMargin: {
          horizontal: 8,
          vertical: 4
        }
      }
    }

    const chart = new ApexCharts(element, options)
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    chart.render()
    chartRef.current = chart

    return () => {
      chart.destroy()
      chartRef.current = null
    }
  }, [id, indicador, dadosOrdenados])

  return <Flex id={`chart-${id}`} />
}
